import { cloneDeep, isEqual } from "lodash";
import { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { Resolver } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import {
  SearchProfileDataFragment,
  SearchProfileOptionDataFragment,
  useGetSearchProfileLazyQuery
} from "@graphql";
import { SearchFilterNames } from "./utils/filters-record";
import { ConsultantFilterState } from "@consultants/consultants-search-new/utils/consultant-filter-state";
import { FiltersPropsRecords } from "./utils/filters-record-types";

type Filters = Array<SearchFilterNames>;

type UseSearchFiltersOptions<T extends object> = {
  initialValues: Partial<ConsultantFilterState>;
  schema?: Resolver<ConsultantFilterState>;
  localStorageKey: string;
  activeFilters: Filters;
  onChange?: (value: T) => void;
  parseSearchProfileFilters?: (filters: Partial<T>) => Partial<T>;
  overrides?: Partial<FiltersPropsRecords>;
};

export type UseSearchFilters<T extends object> = {
  activeFilters: Filters;
  favoriteFilters: Filters;
  setFavoriteFilters: (value: Filters) => void;
  resetFilters: () => void;
  searchProfiles: SearchProfileOptionDataFragment | null;
  setSearchProfiles: (value: SearchProfileOptionDataFragment | null) => void;
  searchProfileFields: string;
  searchProfileFull: SearchProfileDataFragment | null;
  setSearchProfileFull: (value: SearchProfileDataFragment | null) => void;
  filterSliderOpen: boolean;
  setFilterSliderOpen: (value: boolean) => void;
  formMethods: UseFormReturn<T>;
  searchProfileModalProps: SearchProfileModalProps;
  setSearchProfileModalProps: (value: SearchProfileModalProps) => void;

  overrides?: Partial<FiltersPropsRecords>; // todo rework this
};

export type SearchProfileModalProps = {
  open: boolean;
  mode: "new" | "edit";
  currentSearchProfile: SearchProfileOptionDataFragment | null;
};

export function useSearchFilters<T extends object = any>({
  initialValues,
  schema,
  localStorageKey,
  activeFilters,
  parseSearchProfileFilters,
  overrides,
  ...props
}: UseSearchFiltersOptions<T>): UseSearchFilters<any> {
  const [filterSliderOpen, setFilterSliderOpen] = useState(false);

  const prevValues = useRef(initialValues);

  const formMethods = useForm<any>({
    defaultValues: initialValues,
    resolver: schema,
    mode: "onChange"
  });

  const values = formMethods.watch();

  const [_favoriteFilters, _setFavoriteFilters] = useState<Filters>(() => {
    const storedData = localStorage.getItem(localStorageKey);

    return storedData ? JSON.parse(storedData) : [];
  });

  const setFavoriteFilters = (value: Filters) => {
    localStorage.setItem(localStorageKey, JSON.stringify(value));
    _setFavoriteFilters(value);
  };

  const [searchProfiles, setSearchProfiles] =
    useState<SearchProfileOptionDataFragment | null>(null);

  const [searchProfileFull, setSearchProfileFull] =
    useState<SearchProfileDataFragment | null>(null);

  const [searchProfileModalProps, setSearchProfileModalProps] =
    useState<SearchProfileModalProps>({
      open: false,
      mode: "new",
      currentSearchProfile: searchProfileFull
    });

  const [getSearchProfile] = useGetSearchProfileLazyQuery();

  useEffect(() => {
    if (!isEqual(values, prevValues.current)) {
      props.onChange?.(values);
      prevValues.current = cloneDeep(values);
    }
  }, [values, props.onChange]);

  useEffect(() => {
    if (searchProfiles) {
      getSearchProfile({
        variables: {
          id: searchProfiles._id
        },
        onCompleted(data) {
          const searchProfileData = data.searchProfileById;

          if (searchProfileData) {
            const searchProfileFilters = JSON.parse(searchProfileData.fields);

            formMethods.reset({
              ...initialValues,
              ...(parseSearchProfileFilters
                ? parseSearchProfileFilters(searchProfileFilters)
                : searchProfileFilters)
            });

            //todo check if need fire setQueryInput not sure in reset method;
            setSearchProfileFull(searchProfileData);
          }
        }
      });
    }
  }, [searchProfiles]);

  const resetFilters = () => {
    setSearchProfileFull(null);
    setSearchProfiles(null);
    formMethods.reset(initialValues);
  };

  return {
    activeFilters,
    favoriteFilters: _favoriteFilters,
    setFavoriteFilters,
    resetFilters,
    searchProfileFields: JSON.stringify(values),
    searchProfileFull,
    setSearchProfileFull,
    searchProfiles,
    setSearchProfiles,
    filterSliderOpen,
    setFilterSliderOpen,
    searchProfileModalProps,
    setSearchProfileModalProps,
    formMethods,
    overrides
  };
}
